import './WebhookInstructions.css'

const BASE_URL = window.location.origin

function McpInstructions() {
  return (
    <div data-testid="mcp-instructions" className="webhook-instructions">
      <section className="webhook-section">
        <h2 className="webhook-section-title">Connection</h2>

        <div className="mcp-tool" data-testid="mcp-endpoint-url">
          <h3 className="webhook-endpoint-title">Server URL</h3>
          <div className="webhook-method-url">
            <code className="webhook-url">{BASE_URL}/api/mcp</code>
          </div>
          <p className="webhook-description">
            The MCP server uses JSON-RPC 2.0 over HTTP. Send POST requests with a JSON-RPC body to interact with tools.
            A GET request returns server info and a list of available tools.
          </p>
          <div className="webhook-body-label">Example configuration (claude_desktop_config.json):</div>
          <pre className="webhook-code">{JSON.stringify({
            mcpServers: {
              "test-failure-benchmark": {
                url: `${BASE_URL}/api/mcp`
              }
            }
          }, null, 2)}</pre>
        </div>

        <div className="webhook-endpoint" data-testid="mcp-auth-info">
          <h3 className="webhook-endpoint-title">Authentication</h3>
          <p className="webhook-description">
            Read-only tools (list_failures, get_failure, list_runs, etc.) do not require authentication.
            Mutating tools require an <code>x-webhook-secret</code> header matching the server's <code>WEBHOOK_SECRET</code> environment variable.
          </p>
          <p className="webhook-description" style={{ marginTop: '0.5rem' }}>
            Mutating tools that require authentication: <code>update_failure</code>, <code>add_failure_tag</code>, <code>remove_failure_tag</code>, <code>reset_failure_stage</code>, <code>rerun_assessments</code>, <code>rerun_task_assessment</code>, <code>retrigger_task</code>, <code>set_root_cause_locations</code>, <code>add_root_cause_location</code>, <code>remove_root_cause_location</code>, <code>regenerate_test_archive</code>.
          </p>
          <div className="webhook-body-label">Example: calling a mutating tool with authentication:</div>
          <pre className="webhook-code">{`curl -X POST ${BASE_URL}/api/mcp \\
  -H "Content-Type: application/json" \\
  -H "x-webhook-secret: YOUR_SECRET" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 1,
    "method": "tools/call",
    "params": {
      "name": "update_failure",
      "arguments": { "id": "AB-1", "refined_recording_id": "..." }
    }
  }'`}</pre>
          <div className="webhook-body-label">MCP client configuration with authentication headers:</div>
          <pre className="webhook-code">{JSON.stringify({
            mcpServers: {
              "test-failure-benchmark": {
                url: `${BASE_URL}/api/mcp`,
                headers: {
                  "x-webhook-secret": "YOUR_SECRET"
                }
              }
            }
          }, null, 2)}</pre>
        </div>

        <div className="webhook-endpoint" data-testid="mcp-endpoint-initialize">
          <h3 className="webhook-endpoint-title">Initialize</h3>
          <div className="webhook-method-url">
            <span className="webhook-method post">POST</span>
            <code className="webhook-url">{BASE_URL}/api/mcp</code>
          </div>
          <p className="webhook-description">Initialize the MCP session. Returns server capabilities and info.</p>
          <div className="webhook-body-label">Request body (JSON-RPC):</div>
          <pre className="webhook-code">{JSON.stringify({
            jsonrpc: "2.0",
            id: 1,
            method: "initialize",
            params: {}
          }, null, 2)}</pre>
        </div>

        <div className="webhook-endpoint" data-testid="mcp-endpoint-discovery">
          <h3 className="webhook-endpoint-title">Discover Available Tools</h3>
          <div className="webhook-method-url">
            <span className="webhook-method get">GET</span>
            <code className="webhook-url">{BASE_URL}/api/mcp</code>
          </div>
          <p className="webhook-description">Returns server info and a summary of all available tools.</p>
        </div>
      </section>

      <section className="webhook-section">
        <h2 className="webhook-section-title">Tools</h2>

        <div className="mcp-tool" data-testid="mcp-tool-list-failures">
          <h3 className="webhook-endpoint-title">list_failures</h3>
          <p className="webhook-description">List all test failures with their short IDs, titles, tags, and clean branch status.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`tag_name — string (optional) Filter by tag name`}</pre>
        </div>

        <div className="mcp-tool" data-testid="mcp-tool-get-failure">
          <h3 className="webhook-endpoint-title">get_failure</h3>
          <p className="webhook-description">Get detailed information about a specific test failure including recent run results.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`id — string (required) The failure UUID or short_id (e.g. "AB-1")`}</pre>
        </div>

        <div className="mcp-tool" data-testid="mcp-tool-list-runs">
          <h3 className="webhook-endpoint-title">list_runs</h3>
          <p className="webhook-description">List all benchmark runs with their status, agent, and summary statistics.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`status — string (optional) Filter by run status (pending, running, completed, failed)`}</pre>
        </div>

        <div className="mcp-tool" data-testid="mcp-tool-get-run">
          <h3 className="webhook-endpoint-title">get_run</h3>
          <p className="webhook-description">Get detailed information about a specific run including results and containers.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`id — string (required) The run UUID`}</pre>
        </div>

        <div className="mcp-tool" data-testid="mcp-tool-list-agents">
          <h3 className="webhook-endpoint-title">list_agents</h3>
          <p className="webhook-description">List all agents with their accuracy and cost statistics.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`(none)`}</pre>
        </div>

        <div className="mcp-tool" data-testid="mcp-tool-get-task-log">
          <h3 className="webhook-endpoint-title">get_task_log</h3>
          <p className="webhook-description">Get task info and logs for a specific task (one agent + attempt combo for a failure within a run). Use the task_id from get_run results.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`task_id — string (required) The run_tasks UUID (returned as task_id in get_run results)`}</pre>
        </div>

        <div className="mcp-tool" data-testid="mcp-tool-update-failure">
          <h3 className="webhook-endpoint-title">update_failure</h3>
          <p className="webhook-description">Update a failure's metadata fields.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`id                          — string (required) The failure UUID or short_id
short_id                    — string (optional) New short ID
refined_recording_id        — string (optional) Browser recording ID from the refined branch run
refined_playwright_recording_id — string (optional) Playwright test runner recording ID from the refined branch run
refined_error_message       — string (optional) Error message from the refined branch run`}</pre>
        </div>

        <div className="mcp-tool" data-testid="mcp-tool-add-failure-tag">
          <h3 className="webhook-endpoint-title">add_failure_tag</h3>
          <p className="webhook-description">Add a tag to a failure. Creates the tag if it does not exist.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`failure_id — string (required) Failure UUID or short_id
tag_name   — string (required) Tag name to add`}</pre>
        </div>

        <div className="mcp-tool" data-testid="mcp-tool-remove-failure-tag">
          <h3 className="webhook-endpoint-title">remove_failure_tag</h3>
          <p className="webhook-description">Remove a tag from a failure.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`failure_id — string (required) Failure UUID or short_id
tag_name   — string (required) Tag name to remove`}</pre>
        </div>

        <div className="mcp-tool" data-testid="mcp-tool-list-tags">
          <h3 className="webhook-endpoint-title">list_tags</h3>
          <p className="webhook-description">List all tags with their member counts.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`(none)`}</pre>
        </div>

        <div className="mcp-tool" data-testid="mcp-tool-get-recent-events">
          <h3 className="webhook-endpoint-title">get_recent_events</h3>
          <p className="webhook-description">Get the most recent webhook events, optionally filtered by run or container.</p>
          <div className="webhook-body-label">Parameters:</div>
          <pre className="webhook-code">{`run_id         — string (optional) Filter by run UUID
container_name — string (optional) Filter by container name
limit          — number (optional) Max events to return (default 50)`}</pre>
        </div>
      </section>
    </div>
  )
}

export default McpInstructions
